import React, { useEffect, useMemo, useState } from "react"
import PropTypes from "prop-types"
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, Typography } from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import ButtonWithIcon from "src/v2/components/Button/Button"
import DynamicTextarea from "src/v2/components/Input/DynamicTextarea"
import TextFieldInput from "src/v2/components/Input/text-field-input"
import SelectComponent from "src/v2/components/select/select-component"
import { ReactComponent as CheckIcon } from "src/v2/assets/icons/check-icon.svg"
import theme from "src/v2/theme/theme"
import { FIELD_TYPE } from "./company-registration-config"

const booleanOptions = [
    { value: "true", label: "Sim" },
    { value: "false", label: "Não" },
]

const getInitialValues = (fields, row) =>
    fields.reduce((values, field) => {
        const value = row?.[field.name]
        const normalizedValue = field.type === FIELD_TYPE.BOOLEAN && typeof value === "boolean"
            ? String(value)
            : field.type === FIELD_TYPE.DATE && value
                ? `${value}`.slice(0, 10)
                : value ?? ""

        return { ...values, [field.name]: normalizedValue }
    }, {})

const getNormalizedValue = (field, value) => {
    if (value === "") return null
    if (field.type === FIELD_TYPE.BOOLEAN) {
        if (value === true || value === "true") return true
        if (value === false || value === "false") return false
        return null
    }
    if (field.type === FIELD_TYPE.NUMBER) return Number(value)
    return value
}

export const buildRegistrationPayload = (fields, values, changedFieldNames) =>
    fields.reduce((payload, field) => {
        if (changedFieldNames && !changedFieldNames.has(field.name)) return payload

        return {
            ...payload,
            [field.name]: getNormalizedValue(field, values[field.name]),
        }
    }, {})

const isLongTextField = (field) => ["Atividade", "Observacao"].includes(field.name)

const isEmpty = (value) => value === "" || value === null || value === undefined

export const validateRegistrationFields = (fields, values) =>
    fields.reduce((errors, field) => {
        if (field.required && isEmpty(values[field.name])) {
            return { ...errors, [field.name]: "Campo obrigatório." }
        }

        if (
            field.type === FIELD_TYPE.NUMBER &&
            !isEmpty(values[field.name]) &&
            !Number.isFinite(Number(values[field.name]))
        ) {
            return { ...errors, [field.name]: "Informe um número válido." }
        }

        if (field.maxLength !== undefined && `${values[field.name] ?? ""}`.length > field.maxLength) {
            return { ...errors, [field.name]: `Informe no máximo ${field.maxLength} caracteres.` }
        }

        return errors
    }, {})

const CompanyRegistrationForm = ({ open, definition, initialRow, isNew, saving, onClose, onSave }) => {
    const initialValues = useMemo(
        () => getInitialValues(definition.fields, initialRow),
        [definition.fields, initialRow]
    )
    const [values, setValues] = useState(initialValues)
    const [changedFieldNames, setChangedFieldNames] = useState(new Set())
    const [errors, setErrors] = useState({})

    useEffect(() => {
        setValues(initialValues)
        setChangedFieldNames(new Set())
        setErrors({})
    }, [initialValues])

    const sections = useMemo(
        () => definition.fields.reduce((groups, field) => ({
            ...groups,
            [field.section]: [...(groups[field.section] ?? []), field],
        }), {}),
        [definition.fields]
    )

    const updateFieldValue = (field, value) => {
        setValues((currentValues) => ({
            ...currentValues,
            [field.name]: value,
        }))
        setChangedFieldNames((currentFields) => new Set(currentFields).add(field.name))
        setErrors((currentErrors) => {
            if (!currentErrors[field.name]) return currentErrors
            const remainingErrors = { ...currentErrors }
            delete remainingErrors[field.name]
            return remainingErrors
        })
    }

    const handleChange = (field) => (event) => updateFieldValue(field, event.target.value)

    const handleSubmit = async (event) => {
        event.preventDefault()
        const validationErrors = validateRegistrationFields(definition.fields, values)
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors)
            return
        }
        const saveResult = await onSave(
            buildRegistrationPayload(
                definition.fields,
                values,
                isNew ? undefined : changedFieldNames
            )
        )
        if (saveResult?.fieldErrors) {
            setErrors(saveResult.fieldErrors)
            return
        }
        if (saveResult) onClose()
    }

    return (
        <Dialog
            open={open}
            onClose={saving ? undefined : onClose}
            fullWidth
            maxWidth="lg"
            PaperProps={{
                sx: {
                    backgroundColor: theme.palette.background.paper,
                    color: theme.palette.common.white,
                    maxHeight: "calc(100dvh - 32px)",
                },
            }}
        >
            <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, pr: 1.5 }}>
                <Typography component="div" variant="h3" color="common.white">
                    {isNew ? `Adicionar ${definition.title}` : `Editar ${definition.title}`}
                </Typography>
                <IconButton
                    aria-label="Fechar formulário"
                    onClick={onClose}
                    disabled={saving}
                    sx={{
                        color: theme.palette.icon.main,
                        "&:focus-visible": {
                            outline: `2px solid ${theme.palette.common.white}`,
                            outlineOffset: 2,
                        },
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <Box component="form" onSubmit={handleSubmit} noValidate>
                <DialogContent dividers sx={{ borderColor: theme.palette.transparent.white10, py: 2 }}>
                    {Object.entries(sections).map(([section, fields], sectionIndex) => (
                        <Box key={section} component="section" aria-labelledby={`registration-section-${sectionIndex}`} sx={{ mb: sectionIndex === Object.keys(sections).length - 1 ? 0 : 3 }}>
                            <Typography id={`registration-section-${sectionIndex}`} variant="subtitle1" sx={{ color: theme.palette.icon.white70, mb: 1.5 }}>
                                {section}
                            </Typography>
                            <Box sx={{ display: "grid", gridTemplateColumns: { xxxs: "1fr", md: "repeat(2, minmax(0, 1fr))" }, gap: 1.5 }}>
                                {fields.map((field) => (
                                    <Box
                                        key={field.name}
                                        data-field-name={field.name}
                                        sx={{ minWidth: 0, gridColumn: field.fullWidth ? "1 / -1" : undefined }}
                                    >
                                        {field.type === FIELD_TYPE.BOOLEAN ? (
                                            <SelectComponent
                                                label={field.label}
                                                value={values[field.name] ?? ""}
                                                options={booleanOptions}
                                                onChange={handleChange(field)}
                                                clearable
                                                clearAriaLabel={`Limpar ${field.label}`}
                                                onClear={() => updateFieldValue(field, "")}
                                                error={Boolean(errors[field.name])}
                                                helperText={errors[field.name]}
                                            />
                                        ) : isLongTextField(field) ? (
                                            <DynamicTextarea
                                                label={field.label}
                                                value={values[field.name] ?? ""}
                                                onChange={handleChange(field)}
                                                maxRows={6}
                                                characterLimit={field.maxLength}
                                                required={field.required}
                                                error={Boolean(errors[field.name])}
                                                helperText={errors[field.name]}
                                                readOnly={field.readOnlyOnEdit && !isNew}
                                            />
                                        ) : (
                                            <TextFieldInput
                                                label={field.label}
                                                value={values[field.name] ?? ""}
                                                onChange={handleChange(field)}
                                                fullWidth
                                                type={field.type === FIELD_TYPE.DATE ? "date" : field.type === FIELD_TYPE.NUMBER ? "number" : "text"}
                                                InputLabelProps={field.type === FIELD_TYPE.DATE ? { shrink: true } : undefined}
                                                inputProps={{
                                                    ...(field.type === FIELD_TYPE.NUMBER ? { inputMode: "decimal" } : {}),
                                                    ...(field.maxLength !== undefined ? { maxLength: field.maxLength } : {}),
                                                }}
                                                required={field.required}
                                                error={Boolean(errors[field.name])}
                                                helperText={errors[field.name]}
                                                InputProps={field.readOnlyOnEdit && !isNew ? { readOnly: true } : undefined}
                                            />
                                        )}
                                    </Box>
                                ))}
                            </Box>
                            {sectionIndex < Object.keys(sections).length - 1 && <Divider sx={{ borderColor: theme.palette.transparent.white10, mt: 3 }} />}
                        </Box>
                    ))}
                </DialogContent>

                <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                    <ButtonWithIcon
                        size="md"
                        icon={<CheckIcon />}
                        type="submit"
                        disabled={saving}
                    >
                        {saving ? "SALVANDO..." : isNew ? "ADICIONAR" : "SALVAR"}
                    </ButtonWithIcon>
                </DialogActions>
            </Box>
        </Dialog>
    )
}

CompanyRegistrationForm.propTypes = {
    open: PropTypes.bool.isRequired,
    definition: PropTypes.object.isRequired,
    initialRow: PropTypes.object,
    isNew: PropTypes.bool.isRequired,
    saving: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
}

CompanyRegistrationForm.defaultProps = {
    initialRow: null,
    saving: false,
}

export default CompanyRegistrationForm
